import { AutoresizingTextarea, setRefs } from "@qw/ui";
import { type ComponentProps, type FC, type RefObject, useCallback, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { useChat } from "../../providers";
import type { ChatFormValues } from "./chat-form.types";
import { getChatFormPlaceholder } from "./get-chat-form-placeholder";
import { useChatForm } from "./use-chat-form";

export interface ChatFormTextInputProps extends Omit<ComponentProps<"textarea">, "name" | "value"> {
  ref?: RefObject<HTMLTextAreaElement>;
}

export const ChatFormTextInput: FC<ChatFormTextInputProps> = ({ ref, onKeyDown, onChange, ...props }) => {
  const { control } = useFormContext<ChatFormValues>();
  const { field } = useController({ control, name: "text", defaultValue: "" });

  const { chatSession, isReadOnly, elicitationSubmitPhase, status } = useChat();
  const { textInputRef, isRecording, isProcessing, canEdit, canSubmit, submitForm, cancelRecording } = useChatForm();

  const placeholder = useMemo(
    () =>
      getChatFormPlaceholder({
        hasChatSession: Boolean(chatSession),
        isReadOnly,
        isRecording,
        isProcessing,
        status,
        elicitationSubmitPhase,
        sessionStatus: chatSession?.status,
      }),
    [chatSession, isReadOnly, isRecording, isProcessing, elicitationSubmitPhase, status]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Call the custom onKeyDown handler first if provided
      onKeyDown?.(event);

      // Then handle submit on enter
      if (event.key === "Enter" && !event.shiftKey && !event.defaultPrevented) {
        event.preventDefault();

        if (canSubmit) {
          submitForm();
        }
      }
    },
    [canSubmit, onKeyDown, submitForm]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      // Cancel recording when user manually changes input
      if (isRecording) {
        cancelRecording();
      }

      // Update form value
      field.onChange(event);

      // Call the custom onChange handler if provided
      onChange?.(event);
    },
    [field, onChange, cancelRecording, isRecording]
  );

  return (
    <AutoresizingTextarea
      {...props}
      {...field}
      ref={setRefs(field.ref, textInputRef, ref)}
      disabled={!canEdit}
      placeholder={placeholder}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      className="min-h-0 p-2 pb-1 text-sm placeholder:truncate border-none! bg-transparent! focus:ring-transparent! focus:ring-offset-transparent!"
    />
  );
};
